import * as SliderPrimitive from '@radix-ui/react-slider';
import { AnimatePresence, animate, motion, useMotionValue, useTransform } from 'framer-motion';
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '@/Domain/utils/cn';
import { springs } from '@/Domain/utils/springs';

const THUMB_SIZE = 22;
const THUMB_SIZE_REST = 18;
const TRACK_BG_HEIGHT = 18;
const TRACK_INSET = (THUMB_SIZE - TRACK_BG_HEIGHT) / 2;
const HOVER_TOOLTIP_DELAY_MS = 100;

function valueToPixel(v: number, min: number, max: number, trackWidth: number): number {
  if (max === min) return 0;
  const usable = trackWidth - THUMB_SIZE;
  return ((v - min) / (max - min)) * usable;
}

function pixelToValue(
  px: number,
  min: number,
  max: number,
  step: number,
  trackWidth: number,
): number {
  const usable = trackWidth - THUMB_SIZE;
  if (usable <= 0) return min;
  const raw = (px / usable) * (max - min) + min;
  const snapped = Math.round((raw - min) / step) * step + min;
  return Math.max(min, Math.min(max, snapped));
}

type RadixSliderProps = ComponentPropsWithoutRef<typeof SliderPrimitive.Root>;

export type SliderProps = Omit<RadixSliderProps, 'value' | 'onValueChange' | 'defaultValue'> & {
  value: number[];
  onValueChange: (values: number[]) => void;
  error?: boolean;
};

const Slider = forwardRef<ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      value,
      onValueChange,
      min = 0,
      max = 100,
      step = 1,
      disabled = false,
      error = false,
      className,
      id,
      ...props
    },
    ref,
  ) => {
    const currentValue = value[0] ?? min;

    const trackRef = useRef<HTMLDivElement>(null);
    const trackWidthRef = useRef(0);
    const dragging = useRef(false);
    const valueRef = useRef(currentValue);
    const minRef = useRef(min);
    const maxRef = useRef(max);
    valueRef.current = currentValue;
    minRef.current = min;
    maxRef.current = max;

    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [ready, setReady] = useState(false);
    const [showHoverTooltip, setShowHoverTooltip] = useState(false);
    const [hoverPreview, setHoverPreview] = useState<{
      left: number;
      width: number;
      snappedValue: number;
      cursorX: number;
    } | null>(null);
    const hoverDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (isHovered) {
        hoverDelayRef.current = setTimeout(() => setShowHoverTooltip(true), HOVER_TOOLTIP_DELAY_MS);
      } else {
        if (hoverDelayRef.current) clearTimeout(hoverDelayRef.current);
        setShowHoverTooltip(false);
      }
      return () => {
        if (hoverDelayRef.current) clearTimeout(hoverDelayRef.current);
      };
    }, [isHovered]);

    const motionX = useMotionValue(0);
    const fillWidth = useTransform(motionX, (x) => x + THUMB_SIZE / 2 - TRACK_INSET);

    const initialSyncDone = useRef(false);
    useLayoutEffect(() => {
      const el = trackRef.current;
      if (!el || initialSyncDone.current) return;
      const w = el.getBoundingClientRect().width;
      trackWidthRef.current = w;
      motionX.set(valueToPixel(currentValue, min, max, w));
      initialSyncDone.current = true;
      setReady(true);
    }, [currentValue, max, min, motionX]);

    useEffect(() => {
      const el = trackRef.current;
      if (!el) return;
      const ro = new ResizeObserver(([entry]) => {
        const w = entry.contentRect.width;
        trackWidthRef.current = w;
        if (!dragging.current && initialSyncDone.current) {
          animate(
            motionX,
            valueToPixel(valueRef.current, minRef.current, maxRef.current, w),
            springs.moderate,
          );
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, [motionX]);

    useEffect(() => {
      if (!initialSyncDone.current) return;
      if (dragging.current) return;
      const tw = trackWidthRef.current;
      if (tw <= 0) return;
      animate(motionX, valueToPixel(currentValue, min, max, tw), springs.moderate);
    }, [currentValue, min, max, motionX]);

    const emitChange = useCallback(
      (next: number) => {
        onValueChange([next]);
      },
      [onValueChange],
    );

    const computeHoverPreview = useCallback(
      (cursorX: number, trackWidth: number) => {
        const usable = trackWidth - THUMB_SIZE;
        const rawPx = cursorX - THUMB_SIZE / 2;
        const clampedPx = Math.max(0, Math.min(usable, rawPx));
        const rawVal = usable > 0 ? (clampedPx / usable) * (max - min) + min : min;
        const snappedVal = Math.max(
          min,
          Math.min(max, Math.round((rawVal - min) / step) * step + min),
        );
        const snappedPercent = max === min ? 0 : (snappedVal - min) / (max - min);
        const snappedX = THUMB_SIZE / 2 + snappedPercent * usable;

        const thumbCenter = motionX.get() + THUMB_SIZE / 2;
        const edgeX = snappedVal === min ? 0 : snappedVal === max ? trackWidth : snappedX;
        const left = Math.min(thumbCenter, edgeX);
        const width = Math.abs(edgeX - thumbCenter);
        setHoverPreview({ left, width, snappedValue: snappedVal, cursorX: snappedX });
      },
      [min, max, step, motionX],
    );

    const handlePointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const trackRect = trackRef.current?.getBoundingClientRect();
        if (!trackRect) return;

        const localX = e.clientX - trackRect.left - THUMB_SIZE / 2;
        const clamped = Math.max(0, Math.min(trackRect.width - THUMB_SIZE, localX));
        dragging.current = true;
        setIsPressed(true);

        const snappedValue = pixelToValue(clamped, min, max, step, trackRect.width);
        const snappedPx = valueToPixel(snappedValue, min, max, trackRect.width);
        animate(motionX, snappedPx, springs.moderate);
        emitChange(snappedValue);

        const el = e.currentTarget as HTMLElement;
        if (typeof el.setPointerCapture === 'function') {
          try {
            el.setPointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
        }
      },
      [disabled, min, max, step, motionX, emitChange],
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragging.current) return;
        e.stopPropagation();
        const trackRect = trackRef.current?.getBoundingClientRect();
        if (!trackRect) return;
        const localX = e.clientX - trackRect.left - THUMB_SIZE / 2;
        const clamped = Math.max(0, Math.min(trackRect.width - THUMB_SIZE, localX));
        const snappedValue = pixelToValue(clamped, min, max, step, trackRect.width);
        const snappedPx = valueToPixel(snappedValue, min, max, trackRect.width);
        motionX.set(snappedPx);
        emitChange(snappedValue);
      },
      [min, max, step, motionX, emitChange],
    );

    const handlePointerUp = useCallback(() => {
      if (!dragging.current) return;
      dragging.current = false;
      setIsPressed(false);
      setHoverPreview(null);
      const tw = trackWidthRef.current;
      const snapped = pixelToValue(motionX.get(), min, max, step, tw);
      animate(motionX, valueToPixel(snapped, min, max, tw), springs.moderate);
    }, [min, max, step, motionX]);

    const handleRadixChange = useCallback(
      (values: number[]) => {
        if (dragging.current) return;
        onValueChange(values);
      },
      [onValueChange],
    );

    const fillColor = error ? 'var(--destructive)' : 'var(--muted-foreground)';
    const thumbColor = error ? 'var(--destructive)' : 'var(--background)';
    const thumbBorder = error ? 'var(--destructive)' : 'var(--border)';

    return (
      <div
        className={cn(
          'relative w-full select-none touch-none',
          disabled && 'opacity-50 pointer-events-none',
          className,
        )}
        {...(props as React.HTMLAttributes<HTMLDivElement>)}
      >
        <SliderPrimitive.Root
          ref={ref}
          id={id}
          value={value}
          onValueChange={handleRadixChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className="absolute inset-0 opacity-0 pointer-events-none"
          style={{ height: THUMB_SIZE }}
        >
          <SliderPrimitive.Track className="w-full h-full">
            <SliderPrimitive.Range />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            className="block outline-none"
            style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
            onFocus={(e) => {
              if (e.currentTarget.matches(':focus-visible')) setIsFocused(true);
            }}
            onBlur={() => setIsFocused(false)}
          />
        </SliderPrimitive.Root>

        <div
          ref={trackRef}
          className="relative w-full cursor-ew-resize"
          style={{ height: THUMB_SIZE, opacity: ready ? 1 : 0 }}
          onPointerEnter={() => setIsHovered(true)}
          onPointerLeave={() => {
            setIsHovered(false);
            setHoverPreview(null);
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={(e) => {
            handlePointerMove(e);
            if (!dragging.current) {
              const trackRect = trackRef.current?.getBoundingClientRect();
              if (!trackRect) return;
              const x = Math.max(0, Math.min(trackRect.width, e.clientX - trackRect.left));
              computeHoverPreview(x, trackRect.width);
            }
          }}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div
            className="absolute cursor-ew-resize"
            style={{ left: -8, right: -8, top: 0, bottom: 0 }}
          />

          <AnimatePresence>
            {hoverPreview && showHoverTooltip && !isPressed && (
              <motion.div
                key="hover-tooltip"
                className="absolute -translate-x-1/2 pointer-events-none z-20"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4, transition: { duration: 0.1 } }}
                transition={springs.fast}
                style={{ left: hoverPreview.cursorX, top: -26 }}
              >
                <span className="text-[11px] text-background tabular-nums whitespace-nowrap bg-foreground px-2 py-0.5 rounded-md">
                  {hoverPreview.snappedValue}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          <div
            className="absolute rounded-full overflow-hidden bg-accent"
            style={{
              left: TRACK_INSET,
              right: TRACK_INSET,
              top: (THUMB_SIZE - TRACK_BG_HEIGHT) / 2,
              height: TRACK_BG_HEIGHT,
            }}
          >
            <motion.div
              className="absolute top-0 bottom-0 left-0"
              style={{ width: fillWidth, backgroundColor: fillColor }}
            />

            <motion.div
              className="absolute top-0 bottom-0 pointer-events-none z-[2]"
              initial={false}
              animate={{ opacity: hoverPreview && !isPressed ? 1 : 0 }}
              transition={{ opacity: { duration: 0.12 } }}
              style={{
                left: hoverPreview ? hoverPreview.left - TRACK_INSET : 0,
                width: hoverPreview ? hoverPreview.width : 0,
                backgroundColor: 'color-mix(in srgb, var(--foreground) 20%, transparent)',
              }}
            />
          </div>

          <motion.span
            className="flex items-center justify-center pointer-events-none absolute top-1/2 left-0"
            style={{
              width: THUMB_SIZE,
              height: THUMB_SIZE,
              marginTop: -THUMB_SIZE / 2,
              x: motionX,
              zIndex: 10,
            }}
            initial={false}
          >
            <motion.span
              className="block rounded-full border shadow-sm"
              initial={false}
              animate={{
                width: isPressed ? THUMB_SIZE : THUMB_SIZE_REST,
                height: isPressed ? THUMB_SIZE : THUMB_SIZE_REST,
              }}
              transition={springs.fast}
              style={{
                backgroundColor: thumbColor,
                borderColor: thumbBorder,
              }}
            />
            <motion.span
              className="absolute rounded-full border pointer-events-none"
              initial={false}
              animate={{
                opacity: isFocused ? 1 : 0,
                width: THUMB_SIZE + 6,
                height: THUMB_SIZE + 6,
              }}
              transition={springs.fast}
              style={{ borderColor: error ? 'var(--destructive)' : 'var(--ring)' }}
            />
          </motion.span>
        </div>
      </div>
    );
  },
);

Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
